import { useTranslation } from 'react-i18next';
import { LayersControl, GeoJSON } from 'react-leaflet';
import { Feature, FeatureCollection, Geometry, Point } from 'geojson';
import { PropsWithChildren, ReactElement, useCallback } from 'react';
import * as L from 'leaflet';
import { GeofenceProperties } from '@casops/store/apis/geofencesApi';
import { useGeofenceTooltipEventHandlers } from './useGeofenceTooltipEventHandlers';
import { commonPathDisplay } from '../geofence-layer/GeofenceLayer';

export interface GeofenceLayerGroupV3Props {
  layerNameTrans?: string;
  checked?: boolean;
  geoJson?: FeatureCollection<Geometry, GeofenceProperties>;
  filter?: (feature: Feature<Geometry, GeofenceProperties>) => boolean;
  pathOptions?: L.PathOptions;
}

export function GeofenceLayerGroupV3({
  layerNameTrans,
  checked = true,
  geoJson,
  filter,
  pathOptions = commonPathDisplay,
  children, // Allow passing interactive children to the GeoJSON layer (Tooltip, Popup)
}: PropsWithChildren<GeofenceLayerGroupV3Props>): ReactElement | null {
  const { t } = useTranslation();

  const eventHandlers = useGeofenceTooltipEventHandlers();

  const pointToCircle = useCallback(
    (feature: Feature<Point, GeofenceProperties>, latlng: L.LatLng): L.Layer => {
      return L.circle(latlng, {
        ...pathOptions,
        pmIgnore: true, // Disable geoman interactivity on circles
        radius: feature.properties.radius,
      });
    },
    [pathOptions],
  );

  if (geoJson === undefined) {
    return null;
  }

  const controlName = layerNameTrans ?? 'Model.Plural.Geofence_other';

  return (
    <LayersControl.Overlay
      key={controlName} // The key must change for control name changes to be rendered.
      name={t(controlName)}
      checked={checked}
    >
      <GeoJSON
        data={geoJson}
        eventHandlers={eventHandlers}
        style={pathOptions}
        filter={filter}
        pointToLayer={pointToCircle}
        pmIgnore={true} // Disable geoman interactivity on polygons
      >
        {children}
      </GeoJSON>
    </LayersControl.Overlay>
  );
}

export default GeofenceLayerGroupV3;
